/**
 * Document Intelligence — one processed document and what was read out of it.
 *
 * The document on the left, the extracted data on the right, so a value can be
 * checked against the page it came from without leaving the screen.
 *
 * The same screen serves two states:
 *
 * UNSAVED — straight off the pipeline, reached at /document-intelligence/result
 *   with the result and the file in router state. Nothing is stored yet; the
 *   operator reads it, fixes what the model got wrong, and presses Save, which
 *   is what creates the row. Leaving without saving discards the run, which is
 *   the point — processing a document to see what is in it should not fill
 *   Saved Documents with runs nobody wanted.
 *
 * SAVED — loaded from `document_extraction` by id, which is what makes this
 *   screen survive a reload and lets Saved Documents open it again later.
 *   Corrections here are a separate save of their own.
 *
 * In both states corrections are held on the page until a save, so a mistyped
 * value costs no round trip and one save carries every change.
 */
import { useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { Tag } from 'sharedUi/Tag';
import { Toast } from 'sharedUi/Toast';
import { Button } from 'sharedUi/Button';
import { Input } from 'sharedUi/Input';
import { useBreadcrumbTitle } from 'sharedUi/BreadcrumbContext';
import PermissionGuard from '../../components/guards/PermissionGuard';
import PageSkeleton from '../../components/common/page-skeleton';
import DeleteConfirmDialog from '../../components/dialogs/delete-confirm-dialog';
import { Icon } from '../../components/common/icon';
import { useSecureNavigation } from '../../hooks/useSecureNavigation';
import { useCanDelete, useCanUpdate } from '../../hooks/usePermissions';
import { JOB_NAMES } from '../../constants/jobNames';
import { extractErrorMessage } from '../../utils/error-handler';
import { documentExtractionsService } from '../../services/document-extractions.service';
import {
  DEFAULT_DOCUMENT_LANGUAGE,
  type DocumentProcessingResult,
  type SavedDocument,
  type SavedField,
  languageLabel,
} from '../../types/document-intelligence.types';
import SaveReviewDialog from './save-review-dialog';
import { reviewBeforeSave, toWesternDigits } from './save-review';
import { DOCUMENT_VIEW_BASE_PATH, SAVED_DOCUMENTS_PATH, UPLOAD_DOCUMENT_PATH } from './paths';
import AiFeedbackBar from '../../components/ai-feedback/ai-feedback-bar';

/** What the upload page hands over when it navigates here. */
interface ResultHandover {
  result?: DocumentProcessingResult;
  file?: File;
}

/** A file name without its extension.
 *
 *  What the header shows and what the operator edits. The extension is a fact
 *  about the file, not part of what anyone named it, and putting it in an edit
 *  box invites it to be deleted or mistyped — so it is never displayed here and
 *  the server keeps the stored one on a rename.
 */
const nameWithoutExtension = (fileName: string): string => {
  // lastIndexOf, and not at position 0: "notes.v2.pdf" loses only ".pdf", and
  // a name that merely starts with a dot has no extension to strip.
  const dot = fileName.lastIndexOf('.');
  return dot > 0 ? fileName.slice(0, dot) : fileName;
};

const formatSize = (bytes: number): string =>
  (bytes < 1024 * 1024 ? `${(bytes / 1024).toFixed(0)} KB` : `${(bytes / 1024 / 1024).toFixed(1)} MB`);

const formatCost = (value: number | null | undefined, currency: string | null | undefined): string => {
  if (value === null || value === undefined) return '—';
  const amount = value < 0.01 ? value.toFixed(5) : value.toFixed(4);
  return `${currency === 'USD' ? '$' : `${currency || ''} `}${amount}`;
};

/** How sure the model was, as a tag rather than a bare number. */
const ConfidenceTag = ({ value }: { value: number }) => {
  const pct = Math.round((value ?? 0) * 100);
  const severity = value >= 0.85 ? 'success' : value >= 0.6 ? 'warning' : 'danger';
  return <Tag value={`${pct}%`} severity={severity} />;
};

/** A field as this page holds it: the saved row, plus any unsaved correction. */
interface DraftField extends SavedField {
  /** True while this page holds a change that has not been saved. */
  dirty?: boolean;
  /** The last saved value, so an unsaved change can be undone. */
  savedValue?: string | number | null;
  /** The last saved translation, undone along with the value. */
  savedTranslation?: string | number | null;
}

const asText = (v: string | number | null | undefined): string =>
  (v === null || v === undefined ? '' : String(v));

/** Whether the English line would say anything the value does not.
 *
 *  A date, a reference number or a code translates to itself (bar its digits),
 *  and showing it twice is clutter, so the line appears only when it differs.
 */
const translationAddsSomething = (value: string | number | null | undefined,
  translation: string | number | null | undefined): boolean => {
  const t = asText(translation).trim();
  return !!t && toWesternDigits(asText(value).trim()) !== t;
};

/** The English beside a value — a muted second line, clearly labelled. */
const EnglishLine = ({ text, testId }: { text: string; testId: string }) => (
  <p className="mt-1 flex items-baseline gap-1.5 text-body-sm text-ink-muted" dir="ltr"
    data-testid={testId}>
    <span className="shrink-0 rounded border border-line px-1 text-caption font-semibold text-ink-muted">EN</span>
    <span className="break-words">{text}</span>
  </p>
);

interface FieldRowProps {
  field: DraftField;
  editing: boolean;
  canEdit: boolean;
  onEdit: () => void;
  onCancel: () => void;
  /** `translation` is passed only on a bilingual document. */
  onApply: (value: string, translation?: string) => void;
  onRevert: () => void;
  /** On an unsaved run every value is unsent, so "Unsaved" would say nothing. */
  dirtyLabel: string;
  /** The document is not in English: show, and edit, the English beside the value. */
  bilingual: boolean;
}

const FieldRow = ({ field, editing, canEdit, onEdit, onCancel, onApply, onRevert,
  dirtyLabel, bilingual }: FieldRowProps) => {
  const [draft, setDraft] = useState(asText(field.value));
  const [draftEnglish, setDraftEnglish] = useState(asText(field.translatedValue));

  // A row entering edit starts from what is on screen now, not from whatever
  // was typed and abandoned last time.
  useEffect(() => {
    if (editing) {
      setDraft(asText(field.value));
      setDraftEnglish(asText(field.translatedValue));
    }
  }, [editing, field.value, field.translatedValue]);

  const apply = () => (bilingual ? onApply(draft, draftEnglish) : onApply(draft));
  const keys = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') apply();
    if (e.key === 'Escape') onCancel();
  };

  return (
    <tr className="align-top text-body-sm" data-testid="DocumentResult.Field">
      <td className="px-4 py-2.5">
        <div className="flex flex-col items-start gap-1">
          <span className="font-medium text-ink">{field.fieldName}</span>
          <span className="text-caption text-ink-muted">
            {field.dataType}{field.required ? ' · required' : ''}
          </span>
        </div>
      </td>
      <td className="px-4 py-2.5">
        {editing ? (
          <div className="flex items-start gap-1.5">
            {/* Bilingual: the original and its English are corrected together,
                so a fixed value never sits beside a stale translation. */}
            <div className="flex w-full flex-col gap-1.5">
              <Input
                value={draft}
                autoFocus
                dir="auto"
                placeholder={field.missing ? 'Type the value from the document' : undefined}
                onChange={(value) => setDraft(value)}
                onKeyDown={keys}
                className="w-full"
                testId="DocumentResult.Field.Input"
              />
              {bilingual && (
                <Input
                  value={draftEnglish}
                  dir="ltr"
                  placeholder="English translation"
                  onChange={(value) => setDraftEnglish(value)}
                  onKeyDown={keys}
                  className="w-full"
                  testId="DocumentResult.Field.TranslationInput"
                />
              )}
            </div>
            <button type="button" aria-label="Apply" onClick={apply}
              className="rounded p-1.5 text-success hover:bg-green-50 dark:hover:bg-green-900/20"
              data-testid="DocumentResult.Field.Apply">
              <Icon name="check" size={16} />
            </button>
            <button type="button" aria-label="Cancel" onClick={onCancel}
              className="rounded p-1.5 text-ink-muted hover:bg-gray-100 dark:hover:bg-gray-700"
              data-testid="DocumentResult.Field.Cancel">
              <Icon name="x" size={16} />
            </button>
          </div>
        ) : (
          <div className="group flex items-start justify-between gap-2">
            <div className="min-w-0">
              {field.missing ? (
                <span className={field.required ? 'text-error' : 'text-ink-muted'}>
                  {field.required ? 'Not found' : '—'}
                </span>
              ) : (
                // dir="auto": an Arabic value reads right-to-left as written,
                // and a mixed one (a name beside a reference number) keeps its order.
                <span className="block text-ink break-words" dir="auto">{String(field.value)}</span>
              )}
              {bilingual && !field.missing && translationAddsSomething(field.value, field.translatedValue) && (
                <EnglishLine text={asText(field.translatedValue)} testId="DocumentResult.Field.English" />
              )}
              {/* What the AI read, once a person has changed it and saved. */}
              {!field.dirty && field.editedAt && field.aiValue !== undefined
                && field.aiValue !== field.value && (
                <p className="text-caption text-ink-muted" dir="auto">
                  AI read: {field.aiValue === null ? 'nothing' : String(field.aiValue)}
                </p>
              )}
            </div>
            {canEdit && (
              <button type="button" aria-label={`Edit ${field.fieldName}`} onClick={onEdit}
                className="shrink-0 rounded p-1.5 text-ink-muted opacity-0 transition-opacity hover:bg-gray-100 hover:text-primary focus:opacity-100 group-hover:opacity-100 dark:hover:bg-gray-700"
                data-testid="DocumentResult.Field.Edit">
                <Icon name="pencil" size={14} />
              </button>
            )}
          </div>
        )}
      </td>
      <td className="px-4 py-2.5 text-right whitespace-nowrap">
        {field.dirty ? (
          <span className="inline-flex items-center gap-1">
            <Tag value={dirtyLabel} severity="warning" />
            <button type="button" aria-label={`Undo ${field.fieldName}`} onClick={onRevert}
              className="rounded p-1 text-ink-muted hover:bg-gray-100 hover:text-ink dark:hover:bg-gray-700"
              title="Undo this change" data-testid="DocumentResult.Field.Revert">
              <Icon name="history" size={14} />
            </button>
          </span>
        ) : field.editedAt ? <Tag value="Edited" severity="info" />
          : field.missing ? <span className="text-ink-muted">—</span>
            : <ConfidenceTag value={field.confidence} />}
      </td>
    </tr>
  );
};

/** Dress a fresh pipeline result as a saved document.
 *
 *  Everything below reads one shape, so rather than teaching every panel about
 *  two, an unsaved run is mapped onto the saved one. The fields a run does not
 *  have yet — its id, its checksum, where the file was stored — are the fields
 *  saving fills in, and nothing on this screen reads them.
 */
const asDocument = (result: DocumentProcessingResult, fileUrl: string | null): SavedDocument => {
  const classification = result.classification;
  const extraction = result.extraction;
  return {
    _id: '',
    fileName: result.fileName,
    fileType: result.fileType,
    fileSize: result.fileSize,
    language: result.language || DEFAULT_DOCUMENT_LANGUAGE,
    fileSha256: '',
    storage: null,
    status: 'COMPLETED',
    error: null,
    category: classification?.category || 'Unknown',
    classification: {
      schemaId: classification?.categoryId ?? null,
      schemaUpdatedAt: null,
      category: classification?.category ?? null,
      confidence: classification?.confidence ?? null,
      reason: classification?.reason ?? null,
      answered: classification?.answered ?? null,
      model: classification?.model ?? null,
    },
    summary: extraction?.summary || '',
    translatedSummary: extraction?.translatedSummary ?? null,
    translationError: extraction?.translationError ?? null,
    // aiValue starts as the model's own answer and never moves; corrections
    // change `value` only. That pair is what the save records, so a fix made
    // here before saving is stored as a correction, not as something the AI read.
    fields: (extraction?.fields || []).map((f) => ({ ...f, aiValue: f.value })),
    missingRequired: extraction?.missingRequired || [],
    editedCount: 0,
    reviewedBy: null,
    reviewedAt: null,
    ocr: result.ocr
      ? {
        model: result.ocr.model,
        pageCount: result.ocr.pageCount,
        characters: result.ocr.characters,
        contentFormat: result.ocr.contentFormat,
        latencyMs: result.ocr.latencyMs,
      }
      : null,
    text: result.ocr?.text || '',
    cost: result.cost,
    totalMs: result.totalMs ?? null,
    fileUrl,
    createdAt: new Date().toISOString(),
  };
};

interface DocumentTitleProps {
  name: string;
  canEdit: boolean;
  saving: boolean;
  onRename: (name: string) => void;
}

/** The document's name, editable in place. */
const DocumentTitle = ({ name, canEdit, saving, onRename }: DocumentTitleProps) => {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(name);

  // Reopening starts from the name as it stands now, not from whatever was
  // typed and abandoned last time.
  useEffect(() => { if (editing) setDraft(name); }, [editing, name]);

  const apply = () => {
    const next = draft.trim();
    // An empty box means "I changed my mind", not "call this document nothing".
    if (next && next !== name) onRename(next);
    setEditing(false);
  };

  if (editing) {
    return (
      <div className="flex items-center gap-1.5 min-w-0">
        <Input
          value={draft}
          autoFocus
          maxLength={200}
          onChange={(value) => setDraft(value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter') apply();
            if (e.key === 'Escape') setEditing(false);
          }}
          className="w-64"
          testId="DocumentResult.Name.Input"
        />
        <button type="button" aria-label="Apply name" onClick={apply} disabled={saving}
          className="rounded p-1.5 text-success hover:bg-green-50 disabled:opacity-50 dark:hover:bg-green-900/20"
          data-testid="DocumentResult.Name.Apply">
          <Icon name="check" size={16} />
        </button>
        <button type="button" aria-label="Cancel rename" onClick={() => setEditing(false)}
          className="rounded p-1.5 text-ink-muted hover:bg-gray-100 dark:hover:bg-gray-700"
          data-testid="DocumentResult.Name.Cancel">
          <Icon name="x" size={16} />
        </button>
      </div>
    );
  }

  return (
    <span className="group/name flex items-center gap-1 min-w-0">
      <h1 className="text-title font-semibold text-ink truncate" title={name}>{name}</h1>
      {canEdit && (
        <button type="button" aria-label="Rename document" onClick={() => setEditing(true)}
          className="shrink-0 rounded p-1.5 text-ink-muted opacity-0 transition-opacity hover:bg-gray-100 hover:text-primary focus:opacity-100 group-hover/name:opacity-100 dark:hover:bg-gray-700"
          data-testid="DocumentResult.Name.Edit">
          <Icon name="pencil" size={14} />
        </button>
      )}
    </span>
  );
};

const DocumentResult = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { id, isReady } = useSecureNavigation({
    entity: 'document',
    basePath: DOCUMENT_VIEW_BASE_PATH,
  });

  // Taken once, on mount: the run has to outlive anything that replaces the
  // history entry later, and it is gone for good if this component remounts.
  const [handover] = useState<ResultHandover>(() => (location.state as ResultHandover) || {});
  const unsavedResult = handover.result ?? null;
  const unsavedFile = handover.file ?? null;
  // The URL decides which state this is, not the handover. Both routes render
  // this same component, so React keeps it mounted across the hop from /result
  // to /documents/<id> after a save and the handover is still sitting there —
  // reading it alone left the screen offering to save an already-saved run.
  const unsaved = !id && !!unsavedResult;
  const canUpdate = useCanUpdate(JOB_NAMES.AI_MODEL_MASTER);
  const canDelete = useCanDelete(JOB_NAMES.AI_MODEL_MASTER);
  const toast = useRef<Toast>(null);

  const [fields, setFields] = useState<DraftField[]>([]);
  const [editingField, setEditingField] = useState<string | null>(null);
  const [showText, setShowText] = useState(false);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [reviewOpen, setReviewOpen] = useState(false);
  // An unsaved run has nowhere to put a new name yet, so the page holds it and
  // it goes up with everything else on Save.
  const [draftName, setDraftName] = useState<string | null>(null);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [deleting, setDeleting] = useState(false);

  const { data: saved, isLoading, error, refetch } = useQuery({
    queryKey: ['document-extractions', id],
    queryFn: () => documentExtractionsService.getById(id!, true),
    enabled: isReady && !!id,
  });

  // The browser renders the file the operator just picked, straight from
  // memory: there is nothing on the server to link to until they save.
  const [localFileUrl, setLocalFileUrl] = useState<string | null>(null);
  useEffect(() => {
    if (!unsavedFile) return undefined;
    const url = URL.createObjectURL(unsavedFile);
    setLocalFileUrl(url);
    return () => URL.revokeObjectURL(url);
  }, [unsavedFile]);

  const document = useMemo(
    () => (unsaved ? asDocument(unsavedResult!, localFileUrl) : saved),
    [unsaved, unsavedResult, localFileUrl, saved],
  );
  // What the header shows and the breadcrumb says: the pending name while one
  // is pending, otherwise whatever the document is called.
  const displayName = draftName ?? (document ? nameWithoutExtension(document.fileName) : '');
  useBreadcrumbTitle(displayName || undefined);

  // The saved fields become the page's working copy whenever the document is
  // (re)loaded, dropping any half-finished edit along with the stale data.
  useEffect(() => {
    setFields((document?.fields ?? []).map((f) => ({ ...f })));
    setEditingField(null);
    setDraftName(null);
  }, [document]);

  const applyEdit = (name: string, raw: string, rawEnglish?: string) => {
    const value = raw.trim();
    const english = rawEnglish === undefined ? undefined : rawEnglish.trim();
    setFields((prev) => prev.map((f) => (f.fieldName === name ? {
      ...f,
      // The first change remembers the SAVED value; later ones do not overwrite
      // that memory, or Undo would only step back one edit.
      savedValue: f.dirty ? f.savedValue : f.value,
      savedTranslation: f.dirty ? f.savedTranslation : f.translatedValue,
      value: value === '' ? null : value,
      // An empty value has nothing to translate.
      translatedValue: english === undefined ? f.translatedValue
        : (value === '' || english === '' ? null : english),
      missing: value === '',
      dirty: true,
    } : f)));
    setEditingField(null);
  };

  const revertEdit = (name: string) => {
    setFields((prev) => prev.map((f) => (f.fieldName === name ? {
      ...f,
      value: f.savedValue ?? null,
      translatedValue: f.savedTranslation ?? null,
      missing: f.savedValue === null || f.savedValue === undefined || f.savedValue === '',
      dirty: false,
      savedValue: undefined,
      savedTranslation: undefined,
    } : f)));
  };

  const pending = fields.filter((f) => f.dirty);
  /** Not in English: the English is shown, and edited, beside each value. */
  const bilingual = !!document && (document.language || DEFAULT_DOCUMENT_LANGUAGE) !== DEFAULT_DOCUMENT_LANGUAGE;

  /** Keep this run: upload the file, store the data as it stands on screen. */
  const saveDocument = async () => {
    if (!unsaved || !unsavedResult || !unsavedFile) return;
    try {
      setSaving(true);
      setReviewOpen(false);
      setSaveError(null);
      // What is stored is what the operator is looking at, corrections
      // included — sending the untouched pipeline result would quietly throw
      // their fixes away.
      const payload: DocumentProcessingResult = {
        ...unsavedResult,
        // The extension is kept from the file the operator picked: the header
        // never showed one, so a renamed document must not lose the ".pdf"
        // that decides how it downloads.
        fileName: draftName
          ? `${draftName}${unsavedResult.fileName.slice(nameWithoutExtension(unsavedResult.fileName).length)}`
          : unsavedResult.fileName,
        extraction: unsavedResult.extraction && {
          ...unsavedResult.extraction,
          fields: fields.map(({ fieldName, dataType, required, value, aiValue, translatedValue,
            confidence, missing }) => ({
            fieldName, dataType, required, value, aiValue: aiValue ?? null,
            translatedValue: translatedValue ?? null, confidence, missing,
          })),
          missingRequired: fields.filter((f) => f.required && f.missing).map((f) => f.fieldName),
        },
      };
      const row = await documentExtractionsService.save(unsavedFile, payload);
      // `replace`, so Back goes to Upload rather than to a result screen whose
      // router state has been spent and which would now say there is nothing
      // to show.
      navigate(`${DOCUMENT_VIEW_BASE_PATH}/${row._id}`, { replace: true });
    } catch (err) {
      const message = extractErrorMessage(err, 'Failed to save the document.');
      setSaveError(message);
      toast.current?.show({ severity: 'error', summary: 'Not saved', detail: message, life: 10000 });
    } finally {
      setSaving(false);
    }
  };

  /** What the operator is told about before this run is stored. Recomputed as
   *  they correct things, so fixing the last one empties the dialog. */
  const warnings = useMemo(
    () => (unsaved ? reviewBeforeSave(fields, document?.category ?? '') : []),
    [unsaved, fields, document?.category],
  );

  /** Save straight away when there is nothing to say; otherwise say it first.
   *
   *  The dialog is a warning, never a block: the operator can see the page and
   *  the model cannot, so the last word is theirs.
   */
  const requestSave = () => {
    if (warnings.length === 0) {
      saveDocument();
      return;
    }
    setSaveError(null);
    setReviewOpen(true);
  };

  /** Rename the document.
   *
   *  Unsaved, there is nothing to rename yet, so the name is held and travels
   *  with the save. Saved, it is one small PATCH of its own rather than part
   *  of the corrections save — renaming is not correcting, and making someone
   *  press "Save changes" to commit a title they already confirmed would be a
   *  second confirmation of the same decision.
   */
  const renameDocument = async (name: string) => {
    if (unsaved) {
      setDraftName(name);
      return;
    }
    if (!id) return;
    try {
      setSaving(true);
      await documentExtractionsService.rename(id, name);
      await refetch();
      toast.current?.show({ severity: 'success', summary: 'Renamed', life: 3000,
        detail: `This document is now called "${name}".` });
    } catch (err) {
      toast.current?.show({ severity: 'error', summary: 'Error', life: 10000,
        detail: extractErrorMessage(err, 'Failed to rename the document.') });
    } finally {
      setSaving(false);
    }
  };

  const saveCorrections = async () => {
    if (!id || pending.length === 0) return;
    try {
      setSaving(true);
      await documentExtractionsService.updateFields(id,
        pending.map((f) => ({
          fieldName: f.fieldName,
          value: f.value ?? null,
          // Sent only for a bilingual document, where it was edited alongside.
          ...(bilingual ? { translatedValue: f.translatedValue ?? null } : {}),
        })));
      await refetch();
      toast.current?.show({ severity: 'success', summary: 'Saved', life: 3000,
        detail: `${pending.length} correction(s) saved.` });
    } catch (err) {
      toast.current?.show({ severity: 'error', summary: 'Error', life: 10000,
        detail: extractErrorMessage(err, 'Failed to save the corrections.') });
    } finally {
      setSaving(false);
    }
  };

  const confirmDelete = async () => {
    if (!id) return;
    try {
      setDeleting(true);
      await documentExtractionsService.delete(id);
      navigate(SAVED_DOCUMENTS_PATH);
    } catch (err) {
      setDeleteOpen(false);
      toast.current?.show({ severity: 'error', summary: 'Error', life: 10000,
        detail: extractErrorMessage(err, 'Failed to delete the document.') });
    } finally {
      setDeleting(false);
    }
  };

  const totalCost = useMemo(() => {
    if (!document) return null;
    const parts = [document.cost?.ocr, document.cost?.classify, document.cost?.extract,
      document.cost?.translate]
      .filter((v): v is number => typeof v === 'number');
    return parts.length ? parts.reduce((a, b) => a + b, 0) : null;
  }, [document]);

  if (isLoading) return <PageSkeleton />;

  // Landed on /result with nothing handed over — a reload, a bookmark, or Back
  // onto a spent history entry. A run is not stored until it is saved, so there
  // is genuinely nothing to show and the only way on is to process again.
  if (!id && !document) {
    return (
      <div className="flex flex-col items-center justify-center gap-3 p-16 text-center"
        data-testid="DocumentResult.NoRun">
        <span className="flex h-12 w-12 items-center justify-center rounded-full bg-indigo-50 text-primary dark:bg-indigo-900/30">
          <Icon name="file-text" size={24} />
        </span>
        <div className="flex flex-col gap-1">
          <p className="text-heading font-semibold text-ink">Nothing to show here</p>
          <p className="text-body text-ink-muted max-w-md">
            A processed document is only kept once you save it. Process the document
            again to see its extracted data.
          </p>
        </div>
        <Button label="Upload Document" icon="pi pi-upload"
          onClick={() => navigate(UPLOAD_DOCUMENT_PATH)} testId="DocumentResult.Button.Upload" />
      </div>
    );
  }

  if (error || !document) {
    return (
      <div className="flex flex-col items-center justify-center gap-3 p-16 text-center"
        data-testid="DocumentResult.Empty">
        <span className="flex h-12 w-12 items-center justify-center rounded-full bg-indigo-50 text-primary dark:bg-indigo-900/30">
          <Icon name="file-text" size={24} />
        </span>
        <div className="flex flex-col gap-1">
          <p className="text-heading font-semibold text-ink">Document not found</p>
          <p className="text-body text-ink-muted max-w-md">
            {error ? extractErrorMessage(error, 'It could not be loaded.') : 'It may have been deleted.'}
          </p>
        </div>
        <Button label="Saved Documents" icon="pi pi-folder"
          onClick={() => navigate(SAVED_DOCUMENTS_PATH)} testId="DocumentResult.Button.List" />
      </div>
    );
  }

  const missingRequired = fields.filter((f) => f.required && f.missing);
  const isUnknown = document.category === 'Unknown';

  return (
    <PermissionGuard jobName={JOB_NAMES.AI_MODEL_MASTER} resourceName="Document Intelligence">
      <Toast ref={toast} position="top-right" />
      {/*
        The shell gives this page a fixed area: the window less the 72px header,
        the 56px footer and the 16px of padding the content container adds —
        9rem in total. `h-[…]` sizes the BORDER box, so this page's own p-3 is
        already inside that number and must not be subtracted again.

        Pinning the page to that height and hiding its overflow moves the
        scrolling INTO the two columns, so the document and the data scroll
        independently instead of the whole screen moving as one. Below `md` the
        panes stack and the page scrolls as normal.
      */}
      <div className="p-3 md:flex md:h-[calc(100vh-9rem)] md:flex-col md:overflow-hidden"
        data-testid="SCR-DocumentIntelligence-Result">
        {/* Header */}
        <div className="mb-3 flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between md:shrink-0">
          <div className="flex items-start gap-3 min-w-0">
            <Button aria-label="Back" icon="pi pi-arrow-left" severity="secondary" text rounded
              onClick={() => navigate(unsaved ? UPLOAD_DOCUMENT_PATH : SAVED_DOCUMENTS_PATH)}
              className="p-0 mt-1" style={{ width: '28px', height: '28px' }} />
            <div className="min-w-0">
              <div className="flex flex-wrap items-center gap-2">
                <DocumentTitle
                  name={displayName}
                  canEdit={canUpdate}
                  saving={saving}
                  onRename={renameDocument}
                />
                {isUnknown
                  ? <Tag value="Unidentified" severity="warning" />
                  : <Tag value={document.category} severity="info" />}
                {!isUnknown && document.classification?.confidence !== null && (
                  <ConfidenceTag value={document.classification?.confidence ?? 0} />
                )}
                {document.status === 'FAILED' && <Tag value="Failed" severity="danger" />}
              </div>
              <p className="text-body-sm text-ink-muted">
                {document.ocr?.pageCount ?? 0} page(s) · {formatSize(document.fileSize)}
                {document.totalMs ? ` · processed in ${(document.totalMs / 1000).toFixed(1)}s` : ''}
                {totalCost !== null ? ` · ${formatCost(totalCost, document.cost?.currency)}` : ''}
              </p>
            </div>
          </div>
          <div className="flex shrink-0 flex-wrap gap-2">
            {unsaved ? (
              <>
                <Button label={saving ? 'Saving...' : 'Save document'}
                  icon={saving ? 'pi pi-spin pi-spinner' : 'pi pi-save'} size="small"
                  onClick={requestSave} disabled={saving || !unsavedFile}
                  testId="DocumentResult.Button.Save" />
                <Button label="Discard" icon="pi pi-times" severity="secondary" outlined size="small"
                  onClick={() => navigate(UPLOAD_DOCUMENT_PATH)} disabled={saving}
                  testId="DocumentResult.Button.Discard" />
              </>
            ) : (
              <>
                {pending.length > 0 && (
                  <Button label={saving ? 'Saving...' : `Save ${pending.length} change(s)`}
                    icon={saving ? 'pi pi-spin pi-spinner' : 'pi pi-check'} size="small"
                    onClick={saveCorrections} disabled={saving}
                    testId="DocumentResult.Button.SaveCorrections" />
                )}
                {canDelete && (
                  <Button label="Delete" icon="pi pi-trash" severity="danger" outlined size="small"
                    onClick={() => setDeleteOpen(true)} testId="DocumentResult.Button.Delete" />
                )}
                <Button label="Process another" icon="pi pi-upload" size="small" severity="secondary" outlined
                  onClick={() => navigate(UPLOAD_DOCUMENT_PATH)} testId="DocumentResult.Button.Another" />
              </>
            )}
          </div>
        </div>

        {/* Flex rather than grid from `md`: a grid row takes its height from its
            tallest item, so the data column grew past this container and was
            clipped — it never scrolled. */}
        {unsaved && (
          <div className="mb-3 flex items-start gap-2 rounded-lg border border-amber-200 bg-amber-50 px-4 py-2.5 md:shrink-0 dark:border-amber-800 dark:bg-amber-900/20"
            data-testid="DocumentResult.UnsavedBanner">
            <span className="mt-0.5 shrink-0 text-amber-600 dark:text-amber-400">
              <Icon name="alert-triangle" size={16} />
            </span>
            <p className="text-body-sm text-ink">
              This document has not been saved. Check the extracted data, correct anything
              that is wrong, then press <strong>Save document</strong> — leaving this page
              discards it.
              {warnings.length > 0 && (
                <span>
                  {' '}
                  <button type="button" onClick={() => setReviewOpen(true)}
                    className="font-medium text-primary underline underline-offset-2"
                    data-testid="DocumentResult.Button.ShowWarnings">
                    {warnings.length} thing{warnings.length === 1 ? '' : 's'} need
                    {warnings.length === 1 ? 's' : ''} a look
                  </button>
                  .
                </span>
              )}
              {saveError ? <span className="text-error"> {saveError}</span> : null}
            </p>
          </div>
        )}

        <div className="grid grid-cols-1 gap-3 md:flex md:min-h-0 md:flex-1">
          {/* The document */}
          <div className="flex flex-col overflow-hidden rounded-lg border border-line bg-surface shadow-sm md:min-h-0 md:w-1/2">
            <div className="flex shrink-0 items-center justify-between border-b border-line px-4 py-2.5">
              <h2 className="text-heading font-semibold text-ink">Document</h2>
              {document.fileUrl && (
                <a href={document.fileUrl} target="_blank" rel="noreferrer"
                  className="text-body-sm text-primary hover:underline">Open in a new tab</a>
              )}
            </div>
            {document.fileUrl ? (
              <iframe
                src={document.fileUrl}
                title={document.fileName}
                className="h-[70vh] w-full border-0 bg-canvas md:h-auto md:min-h-0 md:flex-1"
                data-testid="DocumentResult.Viewer"
              />
            ) : (
              <p className="p-6 text-body text-ink-muted">
                The file itself was not stored, so there is nothing to preview.
                {document.error ? ` ${document.error}` : ''}
              </p>
            )}
          </div>

          {/* What was read out of it — a BLOCK with spacing, not a column flex:
              flex children shrink when the container runs short, so the cards
              were squashed and clipped instead of overflowing, which left
              nothing to scroll. */}
          <div className="space-y-3 md:min-h-0 md:w-1/2 md:overflow-y-auto md:pr-1"
            data-testid="DocumentResult.DataColumn">
            {document.summary && (
              <div className="rounded-lg border border-line bg-surface p-4 shadow-sm">
                <h2 className="text-heading font-semibold text-ink mb-1.5">Summary</h2>
                <p className="text-body text-ink" dir="auto" data-testid="DocumentResult.Summary">
                  {document.summary}
                </p>
                {bilingual && translationAddsSomething(document.summary, document.translatedSummary) && (
                  <EnglishLine text={asText(document.translatedSummary)} testId="DocumentResult.Summary.English" />
                )}
              </div>
            )}

            {bilingual && document.translationError && (
              <div className="rounded-lg border border-amber-200 bg-amber-50 px-4 py-2.5 dark:border-amber-800 dark:bg-amber-900/20"
                data-testid="DocumentResult.TranslationUnavailable">
                <p className="text-body-sm text-ink">
                  <strong>English translation unavailable.</strong> The values below are as the
                  document states them; you can add the English by editing a field.
                </p>
              </div>
            )}

            {isUnknown && (
              <div className="rounded-lg border border-amber-200 bg-amber-50 p-4 dark:border-amber-800 dark:bg-amber-900/20">
                <h2 className="text-heading font-semibold text-ink mb-1">Could not identify this document</h2>
                <p className="text-body text-ink-muted">
                  {document.classification?.reason || 'It did not match any active category.'}
                  {document.classification?.answered
                    ? ` The model suggested "${document.classification.answered}", which is not a configured category.`
                    : ''}
                </p>
                <p className="text-body-sm text-ink-muted mt-2">
                  Add or adjust a category in Documents Master — a Category Description is what the
                  AI reads to tell categories apart — then process the document again.
                </p>
              </div>
            )}

            {fields.length > 0 && (
              <div className="rounded-lg border border-line bg-surface shadow-sm overflow-hidden">
                <div className="flex flex-wrap items-center justify-between gap-2 border-b border-line px-4 py-2.5">
                  <h2 className="text-heading font-semibold text-ink">Extracted Data</h2>
                  <span className="text-body-sm text-ink-muted">
                    {fields.length} field(s)
                    {missingRequired.length > 0 && (
                      <span className="text-error">{' '}· {missingRequired.length} required not found</span>
                    )}
                    {pending.length > 0 && (
                      <span>{' '}· {pending.length} {unsaved ? 'corrected' : 'unsaved'}</span>
                    )}
                  </span>
                </div>
                <div className="overflow-x-auto">
                  <table className="w-full text-left">
                    <thead className="bg-canvas">
                      <tr className="text-label text-ink-muted">
                        <th className="px-4 py-2 font-medium">Field</th>
                        <th className="px-4 py-2 font-medium">Value</th>
                        <th className="px-4 py-2 font-medium text-right">Confidence</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-line">
                      {fields.map((f) => (
                        <FieldRow
                          key={f.fieldName}
                          field={f}
                          canEdit={canUpdate}
                          editing={editingField === f.fieldName}
                          onEdit={() => setEditingField(f.fieldName)}
                          onCancel={() => setEditingField(null)}
                          onApply={(value, english) => applyEdit(f.fieldName, value, english)}
                          onRevert={() => revertEdit(f.fieldName)}
                          dirtyLabel={unsaved ? 'Corrected' : 'Unsaved'}
                          bilingual={bilingual}
                        />
                      ))}
                    </tbody>
                  </table>
                </div>
                {!unsaved && pending.length > 0 && (
                  <div className="flex items-center justify-between gap-2 border-t border-line px-4 py-2">
                    <p className="text-caption text-ink-muted">
                      {pending.length} change(s) not saved yet.
                    </p>
                    <Button label={saving ? 'Saving...' : 'Save changes'} size="small"
                      icon={saving ? 'pi pi-spin pi-spinner' : 'pi pi-check'}
                      onClick={saveCorrections} disabled={saving} />
                  </div>
                )}

                {/* Only on a SAVED extraction: an unsaved run is discarded when
                    the page is left, and feedback pointing at a record that will
                    not exist is evidence nobody can reopen. Corrections above and
                    feedback here answer different questions — one fixes this
                    document, the other says the model got it wrong. */}
                {!unsaved && document?._id && (
                  <div className="border-t border-line px-4 py-3">
                    <AiFeedbackBar
                      componentType="DOC_EXTRACTION"
                      componentCode={document.category ? `doc:${document.category}` : undefined}
                      componentId={String(document._id)}
                      componentVersion={document.classification?.model || undefined}
                      runtimeContext={{
                        category: document.category ?? null,
                        fieldCount: fields.length,
                        missingRequired: missingRequired.length,
                        correctedFields: pending.length,
                      }}
                      testId="DocumentResult.Feedback"
                    />
                  </div>
                )}
              </div>
            )}

            <div className="rounded-lg border border-line bg-surface p-4 shadow-sm">
              <h2 className="text-heading font-semibold text-ink mb-2">How this was processed</h2>
              <dl className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-2 text-body-sm">
                <div>
                  <dt className="text-ink-muted">Language</dt>
                  <dd className="text-ink">{languageLabel(document.language)}</dd>
                </div>
                <div>
                  <dt className="text-ink-muted">Read by</dt>
                  <dd className="text-ink">{document.ocr?.model || '—'}</dd>
                </div>
                <div>
                  <dt className="text-ink-muted">AI model</dt>
                  <dd className="text-ink">{document.classification?.model || '—'}</dd>
                </div>
                <div>
                  <dt className="text-ink-muted">Reading cost</dt>
                  <dd className="text-ink">{formatCost(document.cost?.ocr, document.cost?.currency)}</dd>
                </div>
                <div>
                  <dt className="text-ink-muted">AI cost</dt>
                  <dd className="text-ink">
                    {formatCost((document.cost?.classify ?? 0) + (document.cost?.extract ?? 0)
                      + (document.cost?.translate ?? 0) || null,
                      document.cost?.currency)}
                  </dd>
                </div>
              </dl>
              {document.text && (
                <>
                  <button type="button" onClick={() => setShowText((v) => !v)}
                    className="mt-3 text-body-sm text-primary hover:underline"
                    data-testid="DocumentResult.Button.ToggleText">
                    {showText ? 'Hide' : 'Show'} the text the AI read
                  </button>
                  {showText && (
                    <pre className="mt-2 max-h-80 overflow-auto rounded bg-canvas p-3 text-caption text-ink whitespace-pre-wrap">
                      {document.text}
                    </pre>
                  )}
                </>
              )}
            </div>
          </div>
        </div>

        <SaveReviewDialog
          visible={reviewOpen}
          warnings={warnings}
          saving={saving}
          onGoBack={() => setReviewOpen(false)}
          onSaveAnyway={saveDocument}
        />

        <DeleteConfirmDialog
          visible={deleteOpen}
          onCancel={() => setDeleteOpen(false)}
          onDelete={confirmDelete}
          message={`Delete "${displayName}" and its extracted data? The stored file is kept.`}
          loading={deleting}
          testId="DocumentResult.Dialog.Delete"
        />
      </div>
    </PermissionGuard>
  );
};

export default DocumentResult;
